import * as React from "react";
import {cx} from "emotion";

import "./results-section.scss";

import {cs}               from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {Form2}            from "@Common/react/cs-form-2/form2";
import {required}         from "@Common/react/cs-form-2/validators/required";
import {isInteger}        from "@Common/react/cs-form-2/validators/is-integer";
import {isNumber}         from "@Common/react/cs-form-2/validators/is-number";
import {max}              from "@Common/react/cs-form-2/validators/max";
import {min}              from "@Common/react/cs-form-2/validators/min";
import {bindInput}        from "@Common/react/bind-input";
import {cascadeSum}       from "@Common/utils/cascade";
import {chain}            from "@Common/utils/fs";
import {Static2}          from "@Common/react/static-2";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";

import {RadioOption} from "../../../../common/radio-option/radio-option";

export const ResultsSection = ({zipCodes, next}) => cs(
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["ref", (_, next) => Static2({next})],
    ["toggle", (_, next) => UseState({initValue: "less", next})],
    ["data", (_, next) => UseState({initValue: {mailOption: "all"}, next})],
    ["form", ({data}, next) => Form2({
        data,
        showErrors: true,
        fields: {
            mailCount: [
                required,
                isNumber,
                isInteger,
                min(1),
                max(zipCodes ? cascadeSum(zipCodes, '[*].count') : 1),
            ],
        },
        next,
    })],
    ({toggle, data, form, eh, ref}) => next({
        valid: !(data.value.mailOption === "limit" && form.field("mailCount").error),
        customQty: data.value.mailOption === "limit" ? form.field("mailCount").state.value : null,
        ref,
        highlightErrors: () => {
            eh.start();
            ref.get().scrollIntoView({behavior: "smooth", block: "end"});
        },
        render: ({showErrors}) => (
            <div className="results-section-pk9" ref={ref.set}>
                <div className="header">
                    {(() => {
                        const zips = chain(
                            zipCodes,
                            (_) => _.filter(v => v.count > 0),
                            (_) => _.map(v => v.zipCode)
                        );

                        return toggle.value === "less" && zips.length > 8 ? (
                            <div className="title" xd-qa-name="New Homeowners Results For Label" xd-qa-type="label">
                                Results for <span className="zip-codes">
                                    {zips.slice(0,7).join(", ")}...
                                        <span 
                                            className="toggle"
                                            onClick={() => toggle.onChange("more")}
                                        >
                                            +More
                                        </span>
                                </span>
                            </div>
                        ) : (
                            <div className="title" xd-qa-name="New Homeowners Results For Label" xd-qa-type="label">
                                Results for <span className="zip-codes">
                                    {zips.join(", ")}
                                    {zips.length > 8 && <span 
                                        className="toggle"
                                        onClick={() => toggle.onChange("less")}
                                    >
                                        -Less
                                    </span>}
                                </span>
                            </div>
                        )
                    })()}
                    <div className="sub-title" xd-qa-name="New Homeowners Results For Description" xd-qa-type="label">
                        Addresses found within the above parameters. Individual addresses are shown after purchase.
                    </div>
                </div>
                <div className="content">
                    <div className="left">
                        {RadioOption({
                            label: "Mail to All Addresses",
                            selected: data.value.mailOption === "all",
                            onSelect: () => data.onChange({mailOption: "all"}),
                            xdQaName: "New Homeowners Mail to All Addresses",
                        })}
                        {RadioOption({
                            label: "Mail to a Custom Quantity",
                            subtitle: "Qty is divided proportionally among resulting zip codes",
                            selected: data.value.mailOption === "limit",
                            onSelect: () => data.onChange({mailOption: "limit"}),
                            xdQaName: "New Homeowners Mail to a Custom Quantity",
                            extra: chain(
                                form.field("mailCount"),
                                (field) => ({
                                    ...field,
                                    showError: showErrors.value.includes("all") || showErrors.value.includes("qty"),
                                }),
                                (field) => <>
                                    {data.value.mailOption === "limit" && (
                                        <div className="custom-quantity">
                                            <input {...{
                                                autoFocus: true,
                                                placeholder: cascadeSum(zipCodes, '[*].count') + " Maximum",
                                                maxLength: 50,
                                                className: cx(eh.css, {hasError: field.showError && field.error}),
                                                onBlur: () => {
                                                    if (!showErrors.value.includes("qty") && !!field.error) {
                                                        showErrors.onChange([...showErrors.value, "qty"]);
                                                    }
                                                },
                                                ...bindInput(field.state),
                                                "xd-qa-type": "text_field",
                                                "xd-qa-name": "New Homeowners Mail to a Custom Quantity",
                                            }}/>
                                            {field.showError && field.error && (
                                                <div className="error" xd-qa-type="label" xd-qa-name="New Homeowners Mail to a Custom Quantity Error">
                                                    {getErrorMessage(field.error)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            ),
                        })}
                    </div>
                    <div className="right">
                        <div className="value" xd-qa-name="New Homeowners Records Found" xd-qa-type="label">
                            {cascadeSum(zipCodes, '[*].count')}
                        </div>
                        <div className="label" xd-qa-name="New Homeowners Records Found Label" xd-qa-type="label">
                            Records Found
                        </div>
                    </div>
                </div>
            </div>
        )
    })
);

const getErrorMessage = (error) => {
    return messages[error.failedValidator.name]?.(error.failedValidator) ?? error.failedValidator.name;
};

const messages = {
    "required": () => "Required",
    "isNumber": () => `Number Only`,
    "isInteger": () => `Number Only`,
    "min": ({min}) => `${min} Minimum`,
    "max": ({max}) => `${max} Maximum`,
};